import instance from "api/globalaxios";
import { getRealhost } from "slices/disputeSlice";
import {
  getDocMgmtFiles,
  getDocMgmtFolders,
  getDocMgmtFolderById,
  uploadDocMgmtFile,
  createDocMgmtFolder,
  renameDocMgmtFile,
  deleteDocMgmtFile,
  renameDocMgmtFolder,
  deleteDocMgmtFolder,
  downloadDocMgmtFile,
  downloadDocMgmtSelectedFiles,
  getDocMgmtWorkspace,
  paginateWorkspace,
  getPaginationProgress,
  getOcrableFilesList,
  ocrFilesForWorkspace,
  findSearchByFileName,
  findSearchByPagination,
  findSearchInFiles,
  bookmarkFolder,
  generateIndexFile
} from "./DocMgmtApi";
import { triggerFileUploadEmail } from "../service";

// Rows of folders / rows of files shown on one page of the Files listing
export const FILES_PAGE_SIZE = 25;

// Used by callers that need every folder/file of a parent (permissions, bookmarking)
const ALL_ROWS_PAGE_SIZE = 1000;

const withPaginationMeta = (res) => {
  const results = res.data?.results || [];
  return {
    ...res,
    data: results,
    count: res.data?.count ?? results.length,
    next: res.data?.next ?? null,
    previous: res.data?.previous ?? null,
  };
};

export const getRequestConfig = (extraParams = {}, extraHeaders = {}) => {
  return {
    params: {
      host: getRealhost(),
      ...extraParams
    },
    headers: {
      ...extraHeaders
    }
  };
};

export async function DownloadFile(file_id) {
  return await downloadDocMgmtFile(file_id, getRequestConfig());
}

export async function getFile(file_id) {
  return await getDocMgmtFolderById(file_id, getRequestConfig());
}

export async function deleteFile(file_id) {
  return await deleteDocMgmtFile(file_id, getRequestConfig());
}

export async function getFiles(
  dispute_id,
  parent_folder_id,
  page = 1,
  pageSize = ALL_ROWS_PAGE_SIZE
) {
  const res = await getDocMgmtFiles(parent_folder_id, page, pageSize, getRequestConfig());
  return withPaginationMeta(res);
}

export async function UploadFiles(
  dispute_id,
  parent_folder_id,
  file,
  viewPermissions,
  deletePermissions,
  webnyay_owner,
  custom_dispute_id
) {
  return await uploadDocMgmtFile(
    file,
    parent_folder_id,
    getRequestConfig({ alert: "off", custom_dispute_id }),
    webnyay_owner
  );
}

export async function UpdateFile(file_id, data, alert = "off") {
  return await renameDocMgmtFile(file_id, data.name, getRequestConfig({ alert }));
}

export async function getFolder(folder_id) {
  const res = await getDocMgmtFolderById(folder_id, getRequestConfig());
  return {
    ...res,
    data: Array.isArray(res.data) ? res.data : [res.data]
  };
}

export async function deleteFolder(folder_id) {
  return await deleteDocMgmtFolder(folder_id, getRequestConfig());
}

export async function getFolders(
  dispute_id,
  parent_folder_id,
  page = 1,
  pageSize = ALL_ROWS_PAGE_SIZE
) {
  const res = await getDocMgmtFolders(parent_folder_id, page, pageSize, getRequestConfig());
  return withPaginationMeta(res);
}

export async function UploadFolders(
  dispute_id,
  folder_name,
  parent_folder_id,
  viewPermissions,
  editPermissions,
  webnyay_owner,
  custom_dispute_id
) {
  return await createDocMgmtFolder(
    folder_name,
    parent_folder_id,
    getRequestConfig({ alert: "off", custom_dispute_id }),
    webnyay_owner
  );
}

// Keep this pointing to Webnyay Main Backend since it manages Dispute profiles
export async function getProfilesOfDispute(dispute_id) {
  return await instance.get(`api/tiacrvprofileedetailsbyid/${dispute_id}/`, getRequestConfig());
}

export async function UpdateFolder(folder_id, data, alert = "off") {
  return await renameDocMgmtFolder(folder_id, data.nameToShowUser || data.name, getRequestConfig({ alert }));
}

export async function DownloadSelectedFiles(data) {
  const response = await downloadDocMgmtSelectedFiles(data.file_ids, data.folder_ids, data.workspace_id, getRequestConfig());
  const blob = new Blob([response.data], { type: "application/zip" });
  const url = window.URL.createObjectURL(blob);

  return {
    data: {
      urls: [url]
    }
  };
}

export async function getDocWorkspace(workspaceId) {
  return await getDocMgmtWorkspace(workspaceId, getRequestConfig());
}

export async function paginateDocWorkspace(workspaceId, folderId, paginationType) {
  return await paginateWorkspace(workspaceId, paginationType, folderId,  getRequestConfig());
}

export async function getDocPaginationProgress(workspaceId, folderId) {
  return await getPaginationProgress(workspaceId, folderId, getRequestConfig());
}

export async function getDocOcrableFilesList(workspaceId, folderId) {
  return await getOcrableFilesList(workspaceId, folderId, getRequestConfig());
}

export async function ocrDocFilesForWorkspace(workspaceId, folderId, extraParams = {}) {
  return await ocrFilesForWorkspace(workspaceId, folderId, getRequestConfig(extraParams));
}

export async function searchFilesByName(query, startPage, perPage, workspaceId) {
  return await findSearchByFileName(query, startPage, perPage, workspaceId, getRequestConfig());
}

export async function searchFilesByPage(pageNumber, workspaceId) {
  return await findSearchByPagination(pageNumber, workspaceId, getRequestConfig());
}

export async function searchFilesByContent(query, startPage, perPage, workspaceId) {
  return await findSearchInFiles(query, startPage, perPage, workspaceId, getRequestConfig());
}

export async function bookmarkFolderApi(folderId) {
  return await bookmarkFolder(folderId, getRequestConfig());
}

export async function generateDocIndexFile(workspaceId, folderId) {
  return await generateIndexFile(workspaceId, folderId, getRequestConfig());
}

const EMAIL_TRIGGER_PROFILE_TYPES = ["general_rv", "lawyer_rv"];

export function notifyFileUploaded(profile, dispute_id, file_name, folder_name) {
  if (!EMAIL_TRIGGER_PROFILE_TYPES.includes(profile?.profile_type)) return;
  triggerFileUploadEmail({
    dispute_id: Number(dispute_id),
    file_name,
    folder_name: folder_name || "",
  }).catch((err) => {
    // A failed notification must not fail an upload that already succeeded
    console.error("File upload email trigger failed:", err);
  });
}
